/**
 * Shared fetch helper that retries transient failures with exponential backoff.
 *
 * The site-search app talks to the accounts-app cross-origin (preview/prod), and
 * that service can scale to zero — a cold start can drop the first request as a
 * network error or surface a 5xx before the instance is ready. Both the read
 * paths (scoped-key auth that gates the whole results page) and the write paths
 * (follow/star) must tolerate this rather than fail permanently on a single
 * transient error.
 *
 * Retries network/transport errors and 5xx responses. Client errors (4xx) are
 * returned as-is so the caller can fail fast — they will not succeed on a repeat.
 */

const MAX_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 300;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Invokes `attempt` up to 3 times with exponential backoff (300ms, 600ms),
 * retrying on thrown network/transport errors and on 5xx responses. A 4xx
 * response is returned immediately. The last error/response is surfaced once
 * attempts are exhausted.
 */
export async function fetchWithRetry(attempt: () => Promise<Response>): Promise<Response> {
    let lastError: Error | undefined;
    let lastResponse: Response | undefined;

    for (let i = 0; i < MAX_ATTEMPTS; i++) {
        if (i > 0) {
            await delay(BASE_BACKOFF_MS * 2 ** (i - 1));
        }

        let res: Response;
        try {
            res = await attempt();
        } catch (err) {
            // Network/transport failure (e.g. cold-start connection drop) — retryable.
            lastError = err instanceof Error ? err : new Error(String(err));
            continue;
        }

        // 5xx are transient; keep retrying. 2xx/4xx are returned to the caller.
        if (res.status < 500) {
            return res;
        }

        lastResponse = res;
    }

    if (lastResponse) {
        return lastResponse;
    }
    throw lastError ?? new Error("fetchWithRetry: request failed");
}
